import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CompanyInfoCard from "./CompanyInfoCard";
import QuickActions from "./QuickActions";
import AppsServices from "./AppsServices";
import ChildEntities from "./ChildEntities";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEntityDetail,
  fetchEntityLsp,
  fetchGlobalEntityDetails,
} from "../globalEntitiesSlice";
import { getEntityAcess } from "../../settings/users/invite-modal/InviteModalSlice";
import Layout from "../../../Layout";
import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { Skeleton } from "primereact/skeleton";
import LspLinkedContact from "./LspLinkedContact";
import { useFeatureFlag } from "~/config/ff";
import { ExportEntityDataModal } from "~/Pages/MyEntityDashboard/export-modal/ExportEntityDataModal";

const EntityDashboard = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const entityData = useSelector((state) => state.globalEntities.entity_detail);
  const entity_lsp = useSelector((state) => state.globalEntities.entity_lsp);
  const [isFetching, setIsFetching] = useState(true);
  const [loading, setLoading] = useState(true);
  const { role, id: userId, isAdmin } = useSelector((state) => state.login);
  const { userlsp, service_url_override } = useSelector(
    (state) => state.inviteusers,
  );
  const { value: isLspFeatureEnabled } = useFeatureFlag("lsp", false);

  useEffect(() => {
    if ((userId && role === "LSP_USER") || role === "LSP_MANAGER") {
      dispatch(getEntityAcess(userId));
    }
  }, [userId]);
  useEffect(
    () => {
      setLoading(true);
      setIsFetching(true);
      dispatch(fetchEntityDetail(id));
      dispatch(fetchGlobalEntityDetails(id))
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
      dispatch(fetchEntityLsp(id))
        .then(() => {
          setIsFetching(false);
        })
        .catch(() => {
          setIsFetching(false);
        });
    },
    [dispatch],
    id,
  );
  const hasEntityPlusAccess = entityData?.active_services?.some(
    (e) => e.name === "Entities +",
  );
  const hasPayrollAccess = entityData?.active_services?.some(
    (e) => e.name === "Payroll",
  );

  /** Controls visibility of the export entity data modal (GXM-1878). */
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const allEntities = useSelector((state) => state.globalEntities.entities);
  /**
   * Single-entity users are non-admins who only have access to one entity.
   * They get redirected here from GlobalEnttitiesTable and never see the
   * "My Entities" dashboard where the export button normally lives.
   * We show the export button in Quick Actions for these users (GXM-1878).
   */
  const isSingleEntityUser = !isAdmin && allEntities.length === 1;

  return (
    <Layout
      crumbs={[
        { label: "My Entities", url: "/entities", id: "entities" },
        {
          label: (
            <div className="flex">
              {!loading ? (
                entityData?.name
              ) : (
                <Skeleton width="100px" height="20px" className="mr-1" />
              )}
              {!loading ? (
                " "
              ) : (
                <Skeleton width="10px" height="20px" className="mr-1" />
              )}
              {!loading ? (
                <>
                  (
                  <ReactCountryFlag
                    countryCode={entityData?.country || ""}
                    svg
                    className=""
                  />{" "}
                  {getName(entityData?.country || "")})
                </>
              ) : (
                <Skeleton width="150px" height="20px" className="mr-1" />
              )}
            </div>
          ),
          id: "entity",
        },
      ]}
      SideNavSection={() => (
        <>
          <div className="sidenav-quick-actions">
            {" "}
            <QuickActions
              hasEntityPlusAccess={hasEntityPlusAccess}
              hasPayrollAccess={hasPayrollAccess}
              onDownloadEntityData={
                isSingleEntityUser
                  ? () => setExportModalVisible(true)
                  : undefined
              }
            />{" "}
          </div>
        </>
      )}
    >
      <div className="entity-dashboard">
        <CompanyInfoCard childEntitiesData={entityData} />
        <AppsServices
          data={entityData}
          load={loading}
          lspserviceurl={
            service_url_override || userlsp?.service_url_override || []
          }
        />
        {!loading && entityData?.children?.length > 0 && (
          <ChildEntities childEntitiesData={entityData?.children} />
        )}
        {isLspFeatureEnabled && (
          <LspLinkedContact
            entityLsp={entity_lsp}
            loading={isFetching}
            role={role}
          />
        )}
      </div>
      {/* Export modal for single-entity users (GXM-1878).
          Maps entity detail data to ExportModalEntity shape. Falls back across
          field name variants because the detail API uses different names than
          the list API (e.g. active_services vs services). */}
      {isSingleEntityUser && (
        <ExportEntityDataModal
          visible={exportModalVisible}
          onClose={() => setExportModalVisible(false)}
          entities={
            entityData
              ? [
                  {
                    id: entityData.id ?? id,
                    name: entityData.name ?? entityData.registered_name ?? null,
                    country:
                      entityData.country ??
                      entityData.country_of_incorporation ??
                      null,
                    type: entityData.type ?? null,
                    status: entityData.status ?? null,
                    services:
                      entityData.services ??
                      entityData.active_services ??
                      undefined,
                  },
                ]
              : []
          }
          isLoading={loading}
        />
      )}
    </Layout>
  );
};
export default EntityDashboard;
